/**
 * Capstone Project Framework - TypeScript Interfaces
 * Based on UC Berkeley Data Strategy Program
 */

export enum CapstoneProjectStatus {
  DRAFT = 'draft',
  IN_PROGRESS = 'in-progress',
  UNDER_REVIEW = 'under-review',
  COMPLETED = 'completed'
}

export enum ModuleStatus {
  NOT_STARTED = 'not-started',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed'
}

export enum DeliverableStatus {
  NOT_STARTED = 'not-started',
  IN_PROGRESS = 'in-progress',
  SUBMITTED = 'submitted',
  REVIEWED = 'reviewed',
  APPROVED = 'approved'
}

export interface CapstoneTimeline {
  startDate: Date | string;
  targetCompletionDate: Date | string;
  actualCompletionDate?: Date | string;
  estimatedHoursPerWeek: number;
}

export interface DeliverableReview {
  reviewerId: string;
  reviewerName?: string;
  score: number; // 1-5
  feedback: string;
  reviewDate: Date | string;
}

export interface DeliverableComment {
  userId: string;
  userName: string;
  comment: string;
  timestamp: Date | string;
}

export interface DeliverableVersion {
  versionNumber: number;
  content: string;
  savedDate: Date | string;
  savedBy: string;
  savedByName?: string;
}

export interface CapstoneDeliverable {
  deliverableId: string;
  name: string;
  description: string;
  templateId?: string;
  status: DeliverableStatus;
  content: string;
  attachments: string[];
  submittedDate?: Date | string;
  reviewedDate?: Date | string;
  review?: DeliverableReview;
  comments: DeliverableComment[];
  versions: DeliverableVersion[];
}

export interface CapstoneModule {
  moduleNumber: number; // 1-7
  moduleName: string;
  moduleDescription: string;
  status: ModuleStatus;
  completionPercentage: number;
  startDate?: Date | string;
  completionDate?: Date | string;
  deliverables: CapstoneDeliverable[];
}

export interface CapstoneTeam {
  members: string[]; // userIds
  memberNames?: string[];
  mentorId?: string;
  mentorName?: string;
  peerReviewers: string[];
  peerReviewerNames?: string[];
}

export interface ROIAnalysis {
  estimatedCostSavings: number;
  estimatedTimeReduction: number;
  qualityImprovement: number;
}

export interface RiskAssessment {
  risk: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  mitigation: string;
}

export interface SuccessMetric {
  metric: string;
  baseline: string;
  target: string;
  measurement: string;
}

export interface FinalPresentation {
  executiveSummary: string;
  strategicRecommendations: string;
  implementationRoadmap: string;
  roiAnalysis: ROIAnalysis;
  riskAssessment: RiskAssessment[];
  successMetrics: SuccessMetric[];
}

export interface CapstoneCompletion {
  isCompleted: boolean;
  completionDate?: Date | string;
  certificateIssued: boolean;
  certificateId?: string;
  overallScore?: number;
}

export interface CapstoneMetadata {
  createdAt: Date | string;
  updatedAt: Date | string;
  lastAccessedAt: Date | string;
  totalHoursInvested: number;
}

export interface CaseStudy {
  organization: string;
  focus: string;
  insights: string;
  color: string;
}

export interface CapstoneProject {
  _id?: string;
  projectId: string;
  userId: string;
  userName?: string;
  projectName: string;
  uscisUseCase: string;
  description: string;
  status: CapstoneProjectStatus;
  timeline: CapstoneTimeline;
  modules: CapstoneModule[];
  team: CapstoneTeam;
  caseStudies?: CaseStudy[];
  finalPresentation?: FinalPresentation;
  completion: CapstoneCompletion;
  metadata: CapstoneMetadata;
}

// Template interfaces
export interface CapstoneTemplate {
  _id?: string;
  templateId: string;
  name: string;
  description: string;
  moduleNumber: number;
  content: string;
  sections: string[];
  estimatedHours: number;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// Use Case interfaces
export interface USCISUseCase {
  useCaseId: string;
  name: string;
  description: string;
  category: string;
  estimatedDuration: number; // weeks
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  objectives: string[];
  expectedOutcomes: string[];
}

// Analytics interfaces
export interface CapstoneAnalytics {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  averageCompletionTime: number; // weeks
  moduleCompletionRates: number[]; // 7 modules
  averageScore: number;
  totalHoursInvested: number;
}

export interface UserCapstoneProgress {
  userId: string;
  userName: string;
  projectsStarted: number;
  projectsCompleted: number;
  currentProjects: CapstoneProject[];
  totalHoursInvested: number;
  certificatesEarned: number;
  averageScore: number;
}
